import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { formatPrice } from '@utils/format';
import { isEmail, isPhone } from '@utils/helper';
import { useRegisterLogs } from '@services/useRegisterLogs';
import { useData } from '@hooks/useData';
import { SITE_URLS } from '@constants';

export interface ServicesGridProps {
    services: {
        uuid: string;
        name: string;
        description: string;
        price: number | null;
        time: string | null;
        externalLink: string | null;
    }[];
    merchantName: string;
}

export const ServicesGrid: FC<ServicesGridProps> = ({ services, merchantName }) => {
    const { t } = useTranslation();
    const { login } = useData();
    const { request: requestRegisterLog } = useRegisterLogs();

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            const userAgent = navigator.userAgent;
            /* cspell:disable */
            const mobileRegex =
                /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|rim)|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
            const tabletRegex = /android|ipad|playbook|silk/i;
            /* cspell:enable */

            setIsMobile(mobileRegex.test(userAgent) || tabletRegex.test(userAgent));
        };
        checkIsMobile();
    }, []);

    const translations = {
        title: t('services-grid-component.title'),
        book: t('services-grid-component.book'),
    };

    const handleLogClick = (serviceName: string, merchantName: string) => {
        /* cspell:disable */
        let message = `Utilizador anonimo reservou o serviço ${serviceName} da loja ${merchantName}`;
        if (login) {
            message = `Utilizador com email ${login.data?.user?.email ?? 'desconhecido'} reservou o serviço ${serviceName} da loja ${merchantName}`;
        }
        /* cspell:enable */
        requestRegisterLog({
            activity_type: 'service_reservation',
            description: message,
        });
    };

    const formatPhoneNumber = (phoneNumber: string | null): string => {
        if (!phoneNumber) {
            return '';
        }
        const digits = phoneNumber.replace(/\D/g, '');

        if (digits.startsWith('351') && digits.length >= 12) {
            return `+351 ${digits.substring(3, 6)} ${digits.substring(6, 9)} ${digits.substring(9, 12)}`;
        } else if (digits.length === 9) {
            return `${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6, 9)}`;
        }
        return phoneNumber;
    };

    return (
        <Container classes="px-0" customSize="custom-large">
            <div className="position-relative" data-testid="services-grid">
                <Typography
                    classes="mb-3"
                    tagVariant={TypographyTagVariants.h2}
                    appearanceLevel={TypographyAppearanceVariants.level3}>
                    {translations.title}
                </Typography>

                <div className="row">
                    {services.map((service) => (
                        <div key={service.name} className="col-lg-6 col-12 mt-5 pb-5 border-bottom">
                            <div className="d-flex justify-content-between align-items-center" style={{ height: '100%' }}>
                                <div className="flex-grow-1">
                                    <Typography
                                        classes="mb-2"
                                        fontWeight="900"
                                        fontSize="16"
                                        breakpointsStyles={{ md: { fontSize: '18' } }}>
                                        {service.name}
                                    </Typography>
                                    {service.description && (
                                        <Typography fontWeight="400" color="muted">
                                            {service.description}
                                        </Typography>
                                    )}
                                </div>
                                <div className="d-flex align-items-center flex-shrink-0">
                                    <div className="text-end px-3 me-3">
                                        <Typography color="primary" appearanceLevel={TypographyAppearanceVariants.level7}>
                                            {formatPrice(service.price)}
                                        </Typography>
                                        {service.time && (
                                            <Typography fontWeight="400" color="muted">
                                                {service.time}
                                            </Typography>
                                        )}
                                    </div>
                                    <div>
                                        {(() => {
                                            const link = service.externalLink;
                                            const linkIsEmail = isEmail(link || '');
                                            const linkIsPhone = isPhone(link || '');
                                            const linkIsUrl = !linkIsEmail && !linkIsPhone;

                                            if (link && linkIsPhone && !isMobile) {
                                                return (
                                                    <Typography fontWeight="900" fontSize="16" classes="text-primary">
                                                        {formatPhoneNumber(link)}
                                                    </Typography>
                                                );
                                            } else if (link) {
                                                return (
                                                    <Anchor
                                                        mailTo={linkIsEmail ? link : undefined}
                                                        tel={linkIsPhone ? link : undefined}
                                                        href={linkIsUrl ? link : undefined}
                                                        target={linkIsUrl ? '_blank' : '_self'}
                                                        mode={AnchorMode.primary}
                                                        onClick={() => handleLogClick(service.name, merchantName)}>
                                                        {translations.book}
                                                    </Anchor>
                                                );
                                            } else {
                                                return (
                                                    <Anchor
                                                        href={SITE_URLS.servicesCart.replace(':serviceUuid', `${service.uuid}`)}
                                                        mode={AnchorMode.primary}>
                                                        {translations.book}
                                                    </Anchor>
                                                );
                                            }
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Container>
    );
};
